/**
 * Where on the page a piece of feedback points (#13574, part of #13573).
 *
 * An anchor is the selector, kind, viewport rectangle and route captured when
 * a submitter pins feedback to a specific element. This module owns the
 * vocabulary, the bounds and the one intake parser; `FeedbackAnchor` in
 * `prisma/schema.prisma` owns the storage.
 *
 * ## `anchorKind` is a String column, not a Prisma enum
 *
 * The issue body for #13574 specified `enum FeedbackAnchorKind`. The repo
 * default moved the other way first: since #13444 an enumerated vocabulary
 * lives in code, not in the database (AGENTS.md, and the argument in full in
 * `src/lib/strategic-plans/vocabularies.ts`). A Postgres enum cannot have a
 * value removed at all and a value added in one transaction cannot be used in
 * that same transaction, so every addition and every rename costs a
 * migration — and this particular vocabulary is a capture-heuristic
 * vocabulary, the kind expected to move faster than a migration (an XPATH or
 * a ROLE kind is an obvious future addition). Repo convention outranks the
 * issue's wording; this comment is the record of the choice.
 *
 * WHAT STILL ENFORCES THE VALUES: {@link parseFeedbackAnchorInput}, at the one
 * boundary that accepts one. The database no longer rejects a bad value, so a
 * write path that skips this parser will persist garbage. Parse at the edge;
 * never hand a bare `string` to Prisma.
 *
 * ## Client-safe
 *
 * zod only — no Prisma import, no `server-only`. The capture UI (a later
 * child of #13573) builds this payload in the browser and must be able to
 * validate it before it submits, against the same bounds the server applies.
 */

import { z } from 'zod'

import { isSafeSubmissionUrl } from './submission-urls'

/**
 * How the element was located, weakest claim LAST.
 *
 *   ID         — the element carried a stable `id`.
 *   TESTID     — an authored `data-testid`. The most durable anchor this app
 *                can produce, because a testid is already load-bearing for
 *                the e2e suite and so is not renamed casually.
 *   ATTR       — some other authored attribute (`data-surface`, `name`, ...).
 *   TEXT       — matched on visible text. Survives a DOM reshuffle, breaks on
 *                a copy edit or a translation.
 *   STRUCTURAL — nth-child / positional. Breaks on any reorder, which is what
 *                `fragile` is usually recording when it is true.
 *
 * The order is documentation, not a ranking anything computes on today.
 */
export const FEEDBACK_ANCHOR_KINDS = [
  'ID',
  'TESTID',
  'ATTR',
  'TEXT',
  'STRUCTURAL',
] as const

export const feedbackAnchorKindSchema = z.enum(FEEDBACK_ANCHOR_KINDS)

export type FeedbackAnchorKind = (typeof FEEDBACK_ANCHOR_KINDS)[number]

/**
 * The fallback for an UNREADABLE stored kind — deliberately the weakest
 * claim, not the strongest.
 *
 * A string column means a row written by an older build, or by hand, can hold
 * a word this vocabulary never had. Reading such a row as `ID` would have a
 * surface assert a precision the row does not substantiate; reading it as
 * STRUCTURAL says only "we located it somehow, treat it as fragile", which is
 * true of every anchor whose provenance we cannot name.
 */
export const DEFAULT_FEEDBACK_ANCHOR_KIND: FeedbackAnchorKind = 'STRUCTURAL'

/** Read a STORED kind back. Never throws — see the constant above. */
export function readFeedbackAnchorKind(
  value: string | null | undefined,
): FeedbackAnchorKind {
  const parsed = feedbackAnchorKindSchema.safeParse(value)
  return parsed.success ? parsed.data : DEFAULT_FEEDBACK_ANCHOR_KIND
}

/**
 * Whether a stored `anchorKind` is one this build actually knows.
 *
 * {@link readFeedbackAnchorKind} deliberately never fails — it falls back to
 * `STRUCTURAL` so an unreadable row still renders. That makes the fallback
 * indistinguishable from a genuine `STRUCTURAL` at the call site, which is
 * fine for display and NOT fine for trust: a consumer deciding whether to
 * re-locate an element needs to know the difference. This is that signal.
 */
export function isKnownFeedbackAnchorKind(
  value: string | null | undefined,
): boolean {
  return feedbackAnchorKindSchema.safeParse(value).success
}

/**
 * Bounds, from the #13574 body. Named here rather than inlined at the call
 * site so the capture UI can render a counter against the same numbers the
 * server rejects on — the drift #7520 removed from the message limits.
 */
export const FEEDBACK_ANCHOR_SELECTOR_MAX_LENGTH = 2000
export const FEEDBACK_ANCHOR_LABEL_MAX_LENGTH = 200
export const FEEDBACK_ANCHOR_ROUTE_MAX_LENGTH = 500
/** The authored `data-surface` value; same order of magnitude as a label. */
export const FEEDBACK_ANCHOR_SURFACE_NAME_MAX_LENGTH = 200

/**
 * Upper bound on how many anchors one route listing returns.
 *
 * `route` is caller-supplied, so the row count on a single path is bounded
 * only by how much feedback has been filed against it — an unbounded
 * `findMany` hands a client the whole history of a busy page in one call.
 * The cap is generous relative to what a viewport can render as pins and is
 * a safety rail, not pagination: if a route ever legitimately exceeds it,
 * the fix is a cursor, not a bigger number.
 */
export const FEEDBACK_ANCHOR_ROUTE_LIST_MAX = 200

/**
 * The thread statuses whose pin is NOT drawn (#13841).
 *
 * Pins are the open-work layer. Once a thread is `RESOLVED` (the PR merged) or
 * `CLOSED` (triaged out without one) its marker is noise on the page it was
 * about — the work is over and the pin is still asking to be read.
 *
 * `DEFERRED` and `FLAGGED_FOR_REVIEW` are deliberately absent. Both are PARKED
 * rather than finished — "someday/maybe", and "held by the intake safety
 * screen" — and they are the threads most likely to need a nudge, so hiding
 * them would take the layer's usefulness with them.
 *
 * NAMED HERE, beside the rest of the anchor vocabulary, because it is applied
 * by BOTH reads in `src/app/actions/feedback-anchors.ts`. Those two differ
 * only in their gate, so a filter inlined into one of them is the drift the
 * two-actions-one-file layout exists to prevent — and it would be invisible,
 * because a member and an admin looking at the same page would simply see
 * different pins.
 *
 * ## The deep link is the THIRD consumer, and it is not a reader of the filter
 *
 * `/feedback`'s "See in context" (#13788) navigates to `?feedback=<id>` and
 * relies on the landing finding that id in the anchor read. So this constant
 * decides which rows may offer that control, and `src/app/feedback/feedback-list.tsx`
 * applies it for exactly that reason. The landing has no not-found affordance
 * — by design, for the permission case — so a terminal-status row reaching it
 * dead-ends silently. Anything else that navigates to a pin by id has the same
 * obligation: check this list first, or give the miss somewhere to land.
 *
 * Typed as the string literals rather than importing Prisma's `FeedbackStatus`:
 * this module is CLIENT-SAFE (zod only, no Prisma import, see the header) and
 * the values are assignable to the generated enum union at the call site.
 */
export const FEEDBACK_PIN_HIDDEN_STATUSES = ['RESOLVED', 'CLOSED'] as const

/**
 * True for an app-relative route and nothing else.
 *
 * Built on `isSafeSubmissionUrl` (#7521) rather than a second regex: that
 * predicate already rejects protocol-relative `//host`, `javascript:`,
 * whitespace, and every character that would break out of the markdown the
 * feedback issue body renders. The extra condition here is that a route —
 * unlike `pageUrl`, which may legitimately be absolute — must start with `/`.
 * It is a GROUPING KEY: `listFeedbackAnchorsForRoute` matches on it exactly,
 * so an origin in the value would fragment one page into several routes.
 */
export function isValidFeedbackAnchorRoute(value: string): boolean {
  if (value.length === 0 || value.length > FEEDBACK_ANCHOR_ROUTE_MAX_LENGTH) {
    return false
  }
  if (!value.startsWith('/')) return false
  return isSafeSubmissionUrl(value)
}

/**
 * The pinned rectangle, viewport-relative.
 *
 * INTEGERS ONLY, and strict about its keys (#13574 edge case 3). `pinRect` is
 * a `Json` column, so whatever passes here is persisted verbatim and read
 * back by a rendering surface — an unvalidated extra key is caller-controlled
 * data smuggled into a store that has no other schema. `z.strictObject`
 * rejects the extra key instead of quietly keeping it.
 *
 * `x`/`y`/`w`/`h` are non-negative; `vw`/`vh` must be positive, because a
 * zero-dimension viewport cannot have produced a capture and is the shape a
 * default-initialized client sends when it never measured anything.
 */
const nonNegativeInt = z.number().int().nonnegative()

export const feedbackAnchorPinRectSchema = z.strictObject({
  x: nonNegativeInt,
  y: nonNegativeInt,
  w: nonNegativeInt,
  h: nonNegativeInt,
  vw: z.number().int().positive(),
  vh: z.number().int().positive(),
})

export type FeedbackAnchorPinRect = z.infer<typeof feedbackAnchorPinRectSchema>

/**
 * The normalized anchor a caller may persist: every optional field resolved
 * to a value or an explicit `null`, so the Prisma write never has to decide.
 */
export interface NormalizedFeedbackAnchor {
  selectorPath: string
  anchorKind: FeedbackAnchorKind
  fragile: boolean
  pinRect: FeedbackAnchorPinRect | null
  route: string
  label: string | null
  surfaceName: string | null
}

/** Discriminated result — this module never throws at a boundary. */
export type FeedbackAnchorParseResult =
  | { ok: true; value: NormalizedFeedbackAnchor }
  | { ok: false; error: string }

/**
 * An optional string that is absent, or present and bounded. Whitespace-only
 * collapses to absent: a label of three spaces is not a label, and storing it
 * would make "has a label" true for a row that renders as nothing.
 */
function optionalBoundedString(
  value: unknown,
  label: string,
  max: number,
): { ok: true; value: string | null } | { ok: false; error: string } {
  if (value === undefined || value === null) return { ok: true, value: null }
  if (typeof value !== 'string') {
    return { ok: false, error: `${label} must be text` }
  }
  if (value.length > max) {
    return { ok: false, error: `${label} must be ${max} characters or less` }
  }
  const trimmed = value.trim()
  return { ok: true, value: trimmed === '' ? null : trimmed }
}

/**
 * Validate and normalize a caller-supplied anchor.
 *
 * Called by `submitFeedback` BEFORE any database operation, per the
 * project's server-action input rule: length first, write second. Returns a
 * submitter-facing message rather than throwing, so the action can keep its
 * `{ success: false, error }` contract.
 *
 * The bound is checked against the RAW string, before trimming, deliberately:
 * a caller who sends 2,001 characters of selector gets told the bound, not
 * silently accepted because 1,000 of them were spaces.
 */
export function parseFeedbackAnchorInput(
  input: unknown,
): FeedbackAnchorParseResult {
  if (input === null || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, error: 'Anchor must be an object' }
  }

  const raw = input as Record<string, unknown>

  if (typeof raw.selectorPath !== 'string') {
    return { ok: false, error: 'Anchor selector is required' }
  }
  if (raw.selectorPath.length > FEEDBACK_ANCHOR_SELECTOR_MAX_LENGTH) {
    return {
      ok: false,
      error: `Anchor selector must be ${FEEDBACK_ANCHOR_SELECTOR_MAX_LENGTH} characters or less`,
    }
  }
  const selectorPath = raw.selectorPath.trim()
  if (selectorPath === '') {
    return { ok: false, error: 'Anchor selector is required' }
  }

  const kind = feedbackAnchorKindSchema.safeParse(raw.anchorKind)
  if (!kind.success) {
    return {
      ok: false,
      error: `Anchor kind must be one of ${FEEDBACK_ANCHOR_KINDS.join(', ')}`,
    }
  }

  if (typeof raw.route !== 'string') {
    return { ok: false, error: 'Anchor route is required' }
  }
  if (raw.route.length > FEEDBACK_ANCHOR_ROUTE_MAX_LENGTH) {
    return {
      ok: false,
      error: `Anchor route must be ${FEEDBACK_ANCHOR_ROUTE_MAX_LENGTH} characters or less`,
    }
  }
  const route = raw.route.trim()
  if (!isValidFeedbackAnchorRoute(route)) {
    return { ok: false, error: 'Anchor route must be an app path' }
  }

  if (raw.fragile !== undefined && typeof raw.fragile !== 'boolean') {
    return { ok: false, error: 'Anchor fragile flag must be true or false' }
  }

  let pinRect: FeedbackAnchorPinRect | null = null
  if (raw.pinRect !== undefined && raw.pinRect !== null) {
    const parsedRect = feedbackAnchorPinRectSchema.safeParse(raw.pinRect)
    if (!parsedRect.success) {
      return {
        ok: false,
        error:
          'Anchor pin rectangle must be x, y, w, h, vw and vh as whole, non-negative numbers',
      }
    }
    pinRect = parsedRect.data
  }

  const label = optionalBoundedString(
    raw.label,
    'Anchor label',
    FEEDBACK_ANCHOR_LABEL_MAX_LENGTH,
  )
  if (!label.ok) return label

  const surfaceName = optionalBoundedString(
    raw.surfaceName,
    'Anchor surface name',
    FEEDBACK_ANCHOR_SURFACE_NAME_MAX_LENGTH,
  )
  if (!surfaceName.ok) return surfaceName

  return {
    ok: true,
    value: {
      selectorPath,
      anchorKind: kind.data,
      fragile: raw.fragile ?? false,
      pinRect,
      route,
      label: label.value,
      surfaceName: surfaceName.value,
    },
  }
}

/**
 * Read a STORED pin rectangle back, or `null`.
 *
 * `null` rather than a default, unlike {@link readFeedbackAnchorKind}, and
 * deliberately so: an unreadable KIND still means "we located the element
 * somehow", but an unreadable RECTANGLE has no safe stand-in. Inventing one
 * would have a surface draw a pin over an element nobody pointed at — the
 * rendering asserting a position it does not have. The column is nullable and
 * every consumer already renders the null case, so falling into it costs
 * nothing.
 */
export function readFeedbackAnchorPinRect(
  value: unknown,
): FeedbackAnchorPinRect | null {
  if (value === null || value === undefined) return null
  const parsed = feedbackAnchorPinRectSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}
